import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Iter 31.217 + 31.214 — MCP status tile.
 *
 * Surfaces the Two-MCP connection state on /admin so Six can see at a
 * glance whether Two has been reading from the ledger over the native
 * connector channel. Auth via the existing handler_context_key.
 *
 * The /api/mcp/status endpoint reports session-transcript and inbound
 * counts as proxies for "Two is alive on the rail". An MCP-flavoured
 * connection itself doesn't leave a row (the SSE handshake is a TCP
 * thing), but tool calls eventually produce ledger rows downstream.
 */
export default function MCPStatusPanel() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [key, setKey] = useState("");
    const [loading, setLoading] = useState(false);

    // Fetch the operator's handler_context_key once so the panel can
    // probe the MCP /status endpoint without the user copy-pasting.
    useEffect(() => {
        let alive = true;
        api.get("/comms/prewarm/key/peek")
            .then(({ data }) => alive && setKey(data?.key || ""))
            .catch(() => {
                // Endpoint may not exist on this build — peek route below.
                api.get("/comms/handler-context")
                    .catch(() => {});
            });
        return () => { alive = false; };
    }, []);

    const probe = async () => {
        setLoading(true);
        setError(null);
        try {
            const k = key || prompt("Paste your handler_context_key (from this page → mint button):");
            if (!k) {
                setLoading(false);
                return;
            }
            // Direct fetch — the /mcp routes are NOT under axios baseURL/api auth.
            const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
            const r = await fetch(`${base}/api/mcp/status`, {
                headers: { Authorization: `Bearer ${k}` },
            });
            if (!r.ok) {
                setError(`HTTP ${r.status} — ${await r.text()}`);
                setLoading(false);
                return;
            }
            setData(await r.json());
            setKey(k);
        } catch (e) {
            setError(String(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="paper-card border border-ink/30 p-4 mb-4"
            data-testid="admin-mcp-status"
        >
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <div>
                    <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink">
                        Two-MCP · Native Connector
                    </h3>
                    <p className="font-body text-xs text-ink-soft mt-1 max-w-2xl">
                        Iter 31.217 — Anthropic-channel access to the ledger.
                        Pair via Claude.ai → Settings → Connectors with
                        URL <code>/api/mcp/sse</code> and Bearer handler_context_key.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={probe}
                    disabled={loading}
                    data-testid="mcp-status-probe-btn"
                    className="font-body text-xs uppercase tracking-[0.18em] px-3 py-1 border border-ink/40 hover:bg-ink/5"
                >
                    {loading ? "Probing…" : data ? "Re-probe" : "Probe"}
                </button>
            </div>
            {error && (
                <div
                    className="font-body text-xs text-stamp-red mt-2"
                    data-testid="mcp-status-error"
                >
                    {error}
                </div>
            )}
            {data && (
                <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 font-body text-xs" data-testid="mcp-status-grid">
                    <div>
                        <dt className="uppercase tracking-[0.18em] text-ink-soft text-[10px]">Service</dt>
                        <dd className="text-ink">{data.service}</dd>
                    </div>
                    <div>
                        <dt className="uppercase tracking-[0.18em] text-ink-soft text-[10px]">Transport</dt>
                        <dd className="text-ink">{data.transport}</dd>
                    </div>
                    <div>
                        <dt className="uppercase tracking-[0.18em] text-ink-soft text-[10px]">Tools</dt>
                        <dd className="text-ink">{data.tools_available}</dd>
                    </div>
                    <div>
                        <dt className="uppercase tracking-[0.18em] text-ink-soft text-[10px]">Endpoint</dt>
                        <dd className="text-ink truncate"><code>{data.endpoint_path}</code></dd>
                    </div>
                    <div>
                        <dt className="uppercase tracking-[0.18em] text-ink-soft text-[10px]">Sessions · 24h</dt>
                        <dd className="text-ink text-base font-heading" data-testid="mcp-sessions-24h">
                            {data.session_transcripts_24h ?? 0}
                        </dd>
                    </div>
                    <div>
                        <dt className="uppercase tracking-[0.18em] text-ink-soft text-[10px]">Inbound · 1h</dt>
                        <dd className="text-ink text-base font-heading" data-testid="mcp-inbound-1h">
                            {data.inbound_last_hour ?? 0}
                        </dd>
                    </div>
                    <div className="col-span-2 sm:col-span-2">
                        <dt className="uppercase tracking-[0.18em] text-ink-soft text-[10px]">Auth model</dt>
                        <dd className="text-ink">{data.auth_model}</dd>
                    </div>
                </dl>
            )}
        </div>
    );
}
